// Окно «Новый пользователь» (T030; contracts/ui.md; ux.md, разделы 12 и 13): ФИО, почта для
// входа и флажок «Администратор» (040, T035; FR-005): выбора ролей и областей нет, флажок снят —
// сотрудник; отмечен — сразу роль «Администратор» на всю установку. Проверка при отправке, ошибка под полем
// с примером, фокус — в первое поле с ошибкой, введённое не стирается; после первой отправки
// поле перепроверяется при вводе, и ошибка уходит, только когда значение прошло схему (FR-030).
// Есть пользователь с тем же ФИО, действующий или в архиве, — подсказка «открыть или вернуть»,
// которая не мешает создать (принцип XVI, T126; GET /api/users?fullName=). Проверка не удалась —
// не молча: выноска «Не удалось проверить, есть ли пользователь с таким ФИО» с причиной, кодом,
// номером операции и «Повторить» (на время повтора — на месте со спиннером, T168); создать можно
// и без неё (T159). Отказ сервера по полям — текст под полем, код и номер
// операции — в выноске над полями; у «почта уже используется» — в подсказке «Открыть
// пользователя» (T058).
// 035 (T017; FR-015, FR-016; ONB-WEL-06): создание ссылку не выпускает — пользователю уходит
// приветственное письмо. После создания — окно «Пользователь добавлен»: строка состояния письма
// (welcome-letter.tsx) обновляется сама. Запасной путь — «Скопировать ссылку» (вторичная): новая
// ссылка (POST /api/users/{id}/invite) в поле «Ссылка-приглашение» и в буфере; подсказка заранее
// говорит, что ссылка из письма перестанет действовать. Поле появляется и тогда, когда буфер обмена
// недоступен: ссылку копируют из него. Ошибка выпуска — тост с причиной, кодом и «Повторить».
// «Готово» — единственная кнопка-акцент, последней справа (README «Цвет»).
import {
  CreateUserBodySchema,
  fieldErrorsFromIssues,
  formatCount,
  nouns,
  roleInfo,
} from '@kp/contracts';
import type { IssuedInvite, User, UserListItem } from '@kp/contracts';
import { Button, Callout, Checkbox, Modal, ModalFooter, TextField } from '@kp/ui';
import { ArchiveRestore, Copy } from '@kp/ui/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { api, apiQuery, isApiError } from '../../api/client.ts';
import { FieldsRefusalCallout } from '../../app/FieldsRefusal.tsx';
import { errorView, hasFieldTexts, useErrorToast } from '../../app/errors.ts';
import type { ErrorView, FieldTexts, FieldsRefusal } from '../../app/errors.ts';
import type { CreateModalProps } from '../../shell/context.tsx';
import { can } from '../../shell/permissions.ts';
import { useMe, useTimeZone } from '../../shell/session.ts';
import { ListLoadError, useQueryFailure } from '../load-error.tsx';
import { emailError, fullNameError } from './fields.ts';
import { InviteLinkField, inviteValidity, useCopyText } from './invite-link.tsx';
import { useRestoreUser } from './user-actions.tsx';
import { MAKE_ADMIN_TEXT } from './UserRoles.tsx';
import { WelcomeLetterStatus } from './welcome-letter.tsx';

type FieldName = 'fullName' | 'email';
const FIELD_ORDER: readonly FieldName[] = ['fullName', 'email'];

/** Поле ответа сервера → поле окна. */
function fieldOf(key: string): FieldName | undefined {
  return key === 'fullName' || key === 'email' ? key : undefined;
}

/** Пояснение флажка «Администратор» — как в окне «Сделать администратором». */
export const ADMIN_HINT = `${MAKE_ADMIN_TEXT}. Без флажка — ${roleInfo.member.inText}`;

function mapFields(fields: Readonly<Record<string, string>>): Partial<Record<FieldName, string>> {
  const result: Partial<Record<FieldName, string>> = {};
  for (const [key, text] of Object.entries(fields)) {
    const field = fieldOf(key);
    if (field && !result[field]) result[field] = text;
  }
  return result;
}

/** Значения формы. */
interface Values {
  fullName: string;
  email: string;
  admin: boolean;
}

interface Existing {
  id: string;
  text: string;
  /** Отказ REC-003: код и номер операции — строкой под подсказкой (T058). */
  view: ErrorView;
}

/** Поиск того же ФИО — после паузы в наборе или сразу при уходе из поля. */
const SAME_NAME_DELAY_MS = 400;
/** Сколько совпадений показывает подсказка. */
const SAME_NAME_LIMIT = 5;

const SAME_NAME_FAILED = 'Не удалось проверить, есть ли пользователь с таким ФИО';

/** Подсказка запасного пути в окне «Пользователь добавлен» (035, FR-016). */
export const WELCOME_FALLBACK_HINT =
  'Письмо не дошло — скопируйте ссылку и передайте сами. Ссылка из письма перестанет действовать';

/** ФИО для поиска совпадений: прошедшее схему, без пробелов по краям; иначе null. */
function lookupName(value: string): string | null {
  const name = value.trim();
  return fullNameError(name) === undefined ? name : null;
}

/** «Морозов О. С. · morozov@example.ru · в архиве». */
function sameNameLine(user: UserListItem): string {
  return [user.fullName, user.email, user.state === 'archived' ? 'в архиве' : null]
    .filter(Boolean)
    .join(' · ');
}

export function NewUserModal({ open, onOpenChange, returnFocusRef }: CreateModalProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const restoreUser = useRestoreUser();
  const copy = useCopyText();
  const timeZone = useTimeZone();
  const me = useMe();
  const { domains } = me;
  const example = domains[0] ?? 'example.ru';

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [admin, setAdmin] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  /** Отказ сервера по полям: код и номер операции — в выноске над полями (T058). */
  const [refusal, setRefusal] = useState<FieldsRefusal<FieldName> | null>(null);
  /** Форму уже отправляли: дальше поля перепроверяются при вводе (FR-030). */
  const [submitted, setSubmitted] = useState(false);
  const [existing, setExisting] = useState<Existing | null>(null);
  const [pending, setPending] = useState(false);
  const [created, setCreated] = useState<{ user: User; letterId: string } | null>(null);
  /** Ссылка «Скопировать ссылку» — выпущена вручную после создания (035, FR-016). */
  const [invite, setInvite] = useState<IssuedInvite | null>(null);
  const [issuing, setIssuing] = useState(false);
  const fallbackHintId = useId();
  const adminHintId = useId();
  const [sameNameOf, setSameNameOf] = useState<string | null>(null);

  const fullNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const linkRef = useRef<HTMLInputElement>(null);
  const doneRef = useRef<HTMLButtonElement>(null);

  // Окно сменилось на «Пользователь добавлен»: кнопки «Создать пользователя» больше нет — фокус
  // на «Готово», а не на странице за окном.
  useEffect(() => {
    if (created) doneRef.current?.focus();
  }, [created]);

  // Совпадение ФИО ищется после паузы в наборе; уход из поля ищет сразу.
  useEffect(() => {
    const timer = window.setTimeout(() => setSameNameOf(lookupName(fullName)), SAME_NAME_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [fullName]);
  const sameName = useQuery({
    ...apiQuery('users.list', { query: { fullName: sameNameOf ?? '', limit: SAME_NAME_LIMIT } }),
    enabled: sameNameOf !== null && created === null && can(me.permissions, ['users.read']),
    staleTime: 30_000,
  });
  const matches = sameNameOf !== null ? (sameName.data?.items ?? []) : [];
  // На время «Повторить» выноска остаётся на месте со спиннером (T168).
  const {
    failure: sameNameFailure,
    retrying: sameNameRetrying,
    retry: retrySameName,
  } = useQueryFailure(sameName, { show: sameNameOf !== null });

  const body = (values: Values) => ({
    fullName: values.fullName,
    email: values.email,
    admin: values.admin,
  });

  /** Проверка формы по той же схеме, что на сервере, и домена — по списку установки. */
  const validate = (values: Values = { fullName, email, admin }) => {
    const parsed = CreateUserBodySchema.safeParse(body(values));
    const found = parsed.success
      ? {}
      : mapFields(fieldErrorsFromIssues(parsed.error.issues, { домен: example }));
    if (!found.email) {
      const wrongDomain = emailError(values.email, domains);
      if (wrongDomain) found.email = wrongDomain;
    }
    return found;
  };

  /**
   * После первой отправки — проверка при вводе: у изменённых полей ошибка уходит, только когда
   * значение прошло схему, и меняется, если стала другой.
   */
  const recheck = (changed: Partial<Values>, fields: readonly FieldName[]) => {
    if (!submitted) return;
    const found = validate({ fullName, email, admin, ...changed });
    setErrors((current) => {
      const next = { ...current };
      for (const field of fields) next[field] = found[field];
      return next;
    });
  };

  const showErrors = (found: Partial<Record<FieldName, string>>) => {
    setErrors(found);
    const first = FIELD_ORDER.find((field) => found[field]);
    const target = { fullName: fullNameRef.current, email: emailRef.current };
    if (first) target[first]?.focus();
  };

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (pending) return;
    setExisting(null);
    setRefusal(null);
    setSubmitted(true);
    const found = validate();
    if (Object.keys(found).length > 0) {
      showErrors(found);
      return;
    }
    setPending(true);
    try {
      const result = await api('users.create', {
        body: { fullName: fullName.trim(), email: email.trim(), admin },
      });
      void queryClient.invalidateQueries({ queryKey: ['api', 'users.list'] });
      setErrors({});
      setCreated({ user: result.user, letterId: result.welcome.letterId });
    } catch (error) {
      const refuse = (fields: FieldTexts<FieldName>) => {
        showErrors(fields);
        setRefusal({ fields, view: errorView(error) });
      };
      const fields = isApiError(error, 'REC-007') ? mapFields(error.fields) : {};
      if (hasFieldTexts(fields)) {
        refuse(fields);
      } else if (isApiError(error, 'REC-004')) {
        refuse({ email: error.nextStep });
      } else if (isApiError(error, 'ACC-009')) {
        refuse({ email: error.nextStep });
      } else if (isApiError(error, 'REC-003')) {
        if (error.body.existingUserId) {
          // Код и номер — в подсказке «Открыть пользователя»: это выноска того же отказа.
          showErrors({ email: error.message });
          setExisting({
            id: error.body.existingUserId,
            text: error.nextStep,
            view: errorView(error),
          });
        } else {
          refuse({ email: error.message });
        }
      } else {
        // Отказ не попал ни в одно поле — тост целиком: причина, шаг, код и номер операции.
        showError(error, () => void submit());
      }
    } finally {
      setPending(false);
    }
  };

  const openExisting = (id: string) => {
    onOpenChange(false);
    void navigate({ to: '/users/$id', params: { id } });
  };

  /**
   * «Вернуть пользователя» из подсказки: сразу, тост «Пользователь возвращён · Отменить» и
   * карточка возвращённого (FR-026б; T134).
   */
  const restoreExisting = async (id: string) => {
    if (await restoreUser(id)) openExisting(id);
  };

  /**
   * «Скопировать ссылку»: первый раз — новая ссылка (прежняя и ссылка из письма перестают
   * действовать), поле со ссылкой и копирование; дальше — копирование той же ссылки.
   */
  const copyLink = async (userId: string): Promise<void> => {
    if (invite) return copy(invite.url, linkRef.current);
    if (issuing) return;
    setIssuing(true);
    let issued: IssuedInvite;
    try {
      issued = await api('users.invite', { params: { id: userId } });
    } catch (error) {
      showError(error, () => void copyLink(userId));
      return;
    } finally {
      setIssuing(false);
    }
    // Поле — до копирования: буфер обмена недоступен — ссылку копируют из поля.
    setInvite(issued);
    await copy(issued.url, linkRef.current);
  };

  if (created) {
    const { user } = created;
    return (
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        title="Пользователь добавлен"
        description={
          invite
            ? `Перешлите ссылку: по ней ${user.fullName} задаст пароль сам. ${inviteValidity(invite, timeZone)}`
            : `${user.fullName} задаст пароль сам по ссылке из письма`
        }
        returnFocusRef={returnFocusRef}
        footer={
          <ModalFooter
            secondary={
              <Button
                icon={Copy}
                loading={issuing}
                aria-describedby={invite ? undefined : fallbackHintId}
                onClick={() => void copyLink(user.id)}
              >
                Скопировать ссылку
              </Button>
            }
            cancelLabel={null}
            primary={
              <Button ref={doneRef} variant="primary" onClick={() => onOpenChange(false)}>
                Готово
              </Button>
            }
          />
        }
      >
        <div className="t-col kp-form">
          <WelcomeLetterStatus
            userId={user.id}
            email={user.email}
            letterId={created.letterId}
            timeZone={timeZone}
          />
          {invite ? (
            <InviteLinkField url={invite.url} inputRef={linkRef} />
          ) : (
            <p id={fallbackHintId} className="t-muted">
              {WELCOME_FALLBACK_HINT}
            </p>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Новый пользователь"
      returnFocusRef={returnFocusRef}
      onSubmitShortcut={() => void submit()}
      footer={
        <ModalFooter
          submitHint
          primary={
            <Button type="submit" form="kp-new-user" variant="primary" loading={pending}>
              Создать пользователя
            </Button>
          }
        />
      }
    >
      <form
        id="kp-new-user"
        className="t-col kp-form"
        noValidate
        onSubmit={(event) => void submit(event)}
      >
        <FieldsRefusalCallout refusal={refusal} errors={errors} />
        <TextField
          ref={fullNameRef}
          label="ФИО"
          placeholder="например, Морозов Олег Сергеевич"
          autoComplete="off"
          required
          value={fullName}
          error={errors.fullName}
          onChange={(event) => {
            setFullName(event.target.value);
            recheck({ fullName: event.target.value }, ['fullName']);
          }}
          onBlur={() => setSameNameOf(lookupName(fullName))}
        />
        {sameNameFailure ? (
          <ListLoadError
            what={SAME_NAME_FAILED}
            view={sameNameFailure}
            retrying={sameNameRetrying}
            onRetry={retrySameName}
          />
        ) : null}
        {matches.length > 0 ? (
          <Callout
            tone="info"
            role="status"
            lead={
              matches.length === 1
                ? 'Есть пользователь с таким ФИО.'
                : `Есть ${formatCount(matches.length, nouns.user)} с таким ФИО.`
            }
          >
            <div className="t-col">
              <span>
                Если это тот же человек — откройте карточку или верните из архива. Если другой —
                создайте пользователя
              </span>
              <ul className="kp-list">
                {matches.map((match) => {
                  const who = `${match.fullName}, ${match.email}`;
                  return (
                    <li key={match.id} className="kp-list-row">
                      <span className="kp-grow">{sameNameLine(match)}</span>
                      {match.state === 'archived' ? (
                        <Button
                          size="sm"
                          icon={ArchiveRestore}
                          aria-label={`Вернуть пользователя из архива: ${who}`}
                          onClick={() => void restoreExisting(match.id)}
                        >
                          Вернуть пользователя
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          aria-label={`Открыть пользователя: ${who}`}
                          onClick={() => openExisting(match.id)}
                        >
                          Открыть пользователя
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </Callout>
        ) : null}
        <TextField
          ref={emailRef}
          label="Почта для входа"
          type="email"
          inputMode="email"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          placeholder={`например, morozov@${example}`}
          required
          value={email}
          error={errors.email}
          onChange={(event) => {
            setEmail(event.target.value);
            setExisting(null);
            recheck({ email: event.target.value }, ['email']);
          }}
        />
        <p className="t-muted">
          По этой почте найдём сотрудника в справочнике. Если его ещё нет — создадим автоматически.
        </p>
        {existing ? (
          <Callout
            tone="warn"
            code={existing.view.code}
            operation={existing.view.operation}
            action={
              <Button size="sm" onClick={() => openExisting(existing.id)}>
                Открыть пользователя
              </Button>
            }
          >
            {existing.text}
          </Callout>
        ) : null}
        <div className="t-col">
          <Checkbox
            label={roleInfo.client_admin.title}
            checked={admin}
            aria-describedby={adminHintId}
            onChange={(event) => setAdmin(event.target.checked)}
          />
          <p id={adminHintId} className="t-muted kp-empty-line">
            {ADMIN_HINT}
          </p>
        </div>
      </form>
    </Modal>
  );
}
